import type { Connection } from "@riea-water/contracts";

/** The prototype's three columns: the utility's systems, the market, then reference and platform. */
export function columnsOf(connections: readonly Connection[]): {
  utility: Connection[];
  market: Connection[];
  referenceAndPlatform: Connection[];
} {
  return {
    utility: connections.filter((c) => c.group === "utility"),
    market: connections.filter((c) => c.group === "market"),
    referenceAndPlatform: connections.filter(
      (c) => c.group === "reference" || c.group === "platform",
    ),
  };
}

/** Health to the prototype's pill class and words (docs/CONNECTIONS.md section 3). */
export function healthPill(health: Connection["health"]): { tone: string; label: string } {
  switch (health) {
    case "connected":
      return { tone: "ok", label: "Connected" };
    case "connected_simulated":
      return { tone: "info", label: "Connected · simulated" };
    case "attention":
      return { tone: "warn", label: "Attention" };
    case "planned":
      return { tone: "off", label: "Planned" };
  }
}

export const DIRECTION: Record<Connection["direction"], string> = {
  in: "→ in",
  out: "out →",
  both: "⇄ two-way",
};

export function summaryCounts(connections: readonly Connection[]): {
  connected: number;
  simulated: number;
  planned: number;
  attention: number;
} {
  return {
    connected: connections.filter((c) => c.health === "connected").length,
    simulated: connections.filter((c) => c.health === "connected_simulated").length,
    planned: connections.filter((c) => c.health === "planned").length,
    attention: connections.filter((c) => c.health === "attention").length,
  };
}

/** "02:04 today", "yesterday 14:10", "9 Oct 2026", or "never". */
export function lastActivity(iso: string | null, now = new Date()): string {
  if (!iso) return "never";
  const at = new Date(iso);
  const time = at.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/London",
  });
  const day = (d: Date) => d.toLocaleDateString("en-GB", { timeZone: "Europe/London" });
  if (day(at) === day(now)) return `${time} today`;
  const yesterday = new Date(now.getTime() - 86_400_000);
  if (day(at) === day(yesterday)) return `yesterday ${time}`;
  return at.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Europe/London",
  });
}

/** The core's module rows, as the prototype shows them, from the modules the tenant has. */
export const CORE_CAPABILITIES = [
  "Registry and event ledger",
  "Rules engine and work routing",
  "Pre-submission gate",
] as const;
